import { useRef, type ClipboardEvent } from 'react';
import './authExperience.css';

const otpLength = 6;

export interface OtpCodeInputProps {
  error: boolean;
  helperText?: string | undefined;
  label: string;
  onBlur: () => void;
  onChange: (value: string) => void;
  value: string;
  autoFocus?: boolean;
}

function sanitizeOtpDigits(value: string) {
  return Array.from(value.normalize('NFKC'))
    .map((character) => {
      const codePoint = character.codePointAt(0);
      if (codePoint === undefined) return '';
      if (codePoint >= 0x660 && codePoint <= 0x669) return String(codePoint - 0x660);
      if (codePoint >= 0x6f0 && codePoint <= 0x6f9) return String(codePoint - 0x6f0);
      return character;
    })
    .join('')
    .replace(/[^0-9]/g, '')
    .slice(0, otpLength);
}

export function OtpCodeInput({
  error,
  helperText,
  label,
  onBlur,
  onChange,
  value,
  autoFocus = false,
}: OtpCodeInputProps) {
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = value
    .padEnd(otpLength, ' ')
    .slice(0, otpLength)
    .split('')
    .map((digit) => digit.trim());

  const focusAfterEnteredCode = (length: number) => {
    const nextIndex = length < otpLength ? length : otpLength - 1;
    inputs.current[nextIndex]?.focus();
  };

  const setDigit = (index: number, input: string) => {
    const sanitized = sanitizeOtpDigits(input);
    if (sanitized.length > 1) {
      onChange(sanitized);
      focusAfterEnteredCode(sanitized.length);
      return;
    }

    const next = [...digits];
    next[index] = sanitized;
    onChange(
      next
        .map((digit) => digit || ' ')
        .join('')
        .trimEnd(),
    );
    if (sanitized && index < otpLength - 1) inputs.current[index + 1]?.focus();
  };

  const setPastedCode = (input: string) => {
    const sanitized = sanitizeOtpDigits(input);
    if (!sanitized) return;
    onChange(sanitized);
    focusAfterEnteredCode(sanitized.length);
  };

  const pasteCode = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    setPastedCode(event.clipboardData.getData('text'));
  };

  const copyCode = (event: ClipboardEvent<HTMLInputElement>) => {
    const code = sanitizeOtpDigits(value);
    if (!code) return;
    event.preventDefault();
    event.clipboardData.setData('text/plain', code);
  };

  return (
    <div role="group" aria-label={label}>
      <span className="auth-otp-label">{label}</span>
      <div className="auth-otp-inputs">
        {digits.map((digit, index) => (
          <input
            key={index}
            className="auth-otp-digit"
            ref={(element) => {
              inputs.current[index] = element;
            }}
            value={digit}
            autoFocus={autoFocus && index === 0}
            aria-label={label + ' digit ' + (index + 1)}
            aria-invalid={error}
            data-filled={digit ? 'true' : 'false'}
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            inputMode="numeric"
            pattern="[0-9]*"
            onCopy={copyCode}
            onPaste={pasteCode}
            onBlur={onBlur}
            onChange={(event) => setDigit(index, event.target.value)}
            onFocus={(event) => event.target.select()}
            onKeyDown={(event) => {
              if (event.key === 'Backspace' && !digits[index] && index > 0)
                inputs.current[index - 1]?.focus();
              if (event.key === 'ArrowLeft' && index > 0) inputs.current[index - 1]?.focus();
              if (event.key === 'ArrowRight' && index < otpLength - 1)
                inputs.current[index + 1]?.focus();
            }}
          />
        ))}
      </div>
      {helperText && (
        <p className="auth-error" role="alert">
          {helperText}
        </p>
      )}
    </div>
  );
}
